import {
  format,
  differenceInDays,
  differenceInMinutes,
  formatDistanceToNowStrict,
  startOfDay,
  endOfDay,
  isSameDay,
  differenceInCalendarDays,
  subDays,
} from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import i18n from 'i18next';

import { getIntegrationByType } from '@/components/organisms/Integrations';
import { getNamedPeriodTitleKey } from '@/modules/analytics/utils';
import { Channel } from '@/shared/types/analytics';
import { parseTimestamp } from '@/util/dateUtils';

export type Bar = { date: string; primary: number; secondary: number; difference?: number };

export type GeneralRow = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

const TEN_DAYS = 10;
const FOURTY_DAYS = 40;
const THREE_DAYS = 3;
const SEVEN_DAYS = 7;

type AnalyticsRange = { startDate: string | null; endDate: string | null };

interface Props {
  currentTotal: GeneralRow[];
  previousTotal?: GeneralRow[];
  shouldComputeDifference?: boolean;
  type?: string;
  disableGrouping?: boolean;
}

export const prepareBar = ({
  currentTotal,
  previousTotal,
  shouldComputeDifference,
  type = 'counts',
  disableGrouping = false,
}: Props) => {
  let newBar: Bar[] = [];

  let currentTotalCounts = 0;
  let previousTotalCounts = 0;

  const firstTypeKey = 'primary';
  const secondTypeKey = 'secondary';
  const keyToUse = type;

  if (currentTotal.length < TEN_DAYS || disableGrouping) {
    for (let i = 0; i < currentTotal.length; i += 1) {
      const currentValue = shouldComputeDifference
        ? currentTotal[i][keyToUse] - previousTotal[i][keyToUse]
        : currentTotal[i][keyToUse];

      const previousValue = previousTotal ? previousTotal[i][keyToUse] : 0;

      newBar = [
        ...newBar,
        {
          date: format(new Date(currentTotal[i].date), 'd MMM'),
          [firstTypeKey]: currentValue,
          [secondTypeKey]: previousValue,
          difference: 1,
        },
      ];
      currentTotalCounts += currentValue;
      if (previousTotal) {
        previousTotalCounts += previousValue;
      }
    }
    return { newBar, currentTotalCounts, previousTotalCounts };
  } else {
    const separator = currentTotal.length < FOURTY_DAYS ? THREE_DAYS : SEVEN_DAYS;

    let currentCounts = 0;
    let previousCount = 0;

    let currentTotalSum = 0;
    let previousTotalSum = 0;

    for (let i = 0; i < currentTotal.length; i += 1) {
      const currentValue = shouldComputeDifference
        ? currentTotal[i][keyToUse] - previousTotal[i][keyToUse]
        : currentTotal[i][keyToUse];

      const previousValue = previousTotal ? previousTotal[i][keyToUse] : 0;

      currentTotalSum += currentValue;

      previousTotalSum += previousValue;

      currentCounts += currentValue;
      previousCount += previousValue;

      const isFirstGroup = i === separator - 1;
      const skipFirst = i % separator === separator - 1 && i !== 0 && i !== separator;
      if (isFirstGroup || skipFirst) {
        let groupStartDate = '';
        let groupEndDate = '';

        groupEndDate = format(new Date(currentTotal[i].date), 'd MMM');
        groupStartDate =
          i === separator
            ? format(new Date(currentTotal[i - separator].date), 'd MMM')
            : format(new Date(currentTotal[i - separator + 1].date), 'd MMM');

        newBar = [
          ...newBar,
          {
            date: `${groupStartDate} - ${groupEndDate}`,
            [firstTypeKey]: currentCounts,
            [secondTypeKey]: previousCount,
            difference:
              differenceInDays(
                new Date(currentTotal[i].date),
                i === separator
                  ? new Date(currentTotal[i - separator].date)
                  : new Date(currentTotal[i - separator + 1].date)
              ) + 1,
          } as unknown as Bar,
        ];

        //If there is not another full group after that
        if (!currentTotal[i + separator]) {
          if (currentTotal[i + 1]) {
            groupStartDate = format(new Date(currentTotal[i + 1].date), 'd MMM');
            groupEndDate = format(new Date(currentTotal[currentTotal.length - 1].date), 'd MMM');

            //If the next item is the last item
            if (i + 1 === currentTotal.length - 1) {
              const currentValue = shouldComputeDifference
                ? currentTotal[i + 1][keyToUse] - previousTotal[i + 1][keyToUse]
                : currentTotal[i + 1][keyToUse];

              const previousValue = previousTotal ? previousTotal[i + 1][keyToUse] : 0;

              newBar = [
                ...newBar,
                {
                  date: groupStartDate,
                  [firstTypeKey]: currentValue,
                  [secondTypeKey]: previousValue,
                  difference: 1,
                },
              ];
            } else {
              currentCounts = 0;
              previousCount = 0;
              let difference = 0;

              for (let j = i + 1; j <= currentTotal.length - 1; j += 1) {
                const currentValue = shouldComputeDifference
                  ? currentTotal[j][keyToUse] - previousTotal[j][keyToUse]
                  : currentTotal[j][keyToUse];

                const previousValue = previousTotal ? previousTotal[j][keyToUse] : 0;

                currentCounts += currentValue;

                previousCount += previousValue;
                difference += 1;
              }
              newBar = [
                ...newBar,
                {
                  date: `${groupStartDate} - ${groupEndDate}`,
                  [firstTypeKey]: currentCounts,
                  [secondTypeKey]: previousCount,
                  difference,
                } as unknown as Bar,
              ];
            }
          }
        }
        currentCounts = 0;
        previousCount = 0;
      }
    }
    return { newBar, currentTotalCounts: currentTotalSum, previousTotalCounts: previousTotalSum };
  }
};

/**
 * Formats session log data for display in UI components.
 * This function safely processes date strings and calculates durations,
 * handling invalid or missing data gracefully to prevent crashes.
 *
 * @param {Object} params - The parameters object
 * @param {Channel} params.channel - The communication channel type (e.g., 'web', 'whatsapp')
 * @param {string} params.start_time - The session start time as a date string
 * @param {string} params.end_time - The session end time as a date string
 * @param {string} [params.expired_time] - Optional session expiration time as a date string
 *
 * @returns {Object} Formatted log data
 * @returns {React.Component|undefined} returns.Icon - Channel-specific icon component
 * @returns {string|number} returns.duration - Session duration in minutes or "0 sec" if invalid
 * @returns {number|undefined} returns.sessionDuration - Session duration until expiration (if expired_time provided)
 * @returns {string} returns.formattedTime - Formatted start time as "yyyy-MM-dd HH:mm:ss" or empty string if invalid
 *
 * @example
 * // Valid session data
 * const result = formatLogs({
 *   channel: 'web',
 *   start_time: '2024-01-15T10:30:00Z',
 *   end_time: '2024-01-15T10:35:00Z',
 *   expired_time: '2024-01-15T10:40:00Z'
 * });
 * // Returns: { Icon: WebIcon, duration: 5, sessionDuration: 10, formattedTime: '2024-01-15 10:30:00' }
 *
 * @example
 * // Invalid/missing data (graceful handling)
 * const result = formatLogs({
 *   channel: 'web',
 *   start_time: undefined,
 *   end_time: 'invalid-date',
 *   expired_time: null
 * });
 * // Returns: { Icon: WebIcon, duration: '0 sec', sessionDuration: undefined, formattedTime: '' }
 */
export const formatLogs = ({
  channel,
  start_time,
  end_time,
  expired_time,
}: {
  channel: Channel;
  start_time: string;
  end_time: string;
  expired_time?: string;
}) => {
  const Icon = getIntegrationByType(channel)?.Icon;

  // Helper function to creates a valid Date object or returns null if invalid
  const createValidDate = (dateString: string | undefined): Date | null => {
    if (!dateString || typeof dateString !== 'string') {
      return null;
    }
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };
  // Create valid Date objects
  const startDate = createValidDate(start_time);
  const endDate = createValidDate(end_time);
  const expiredDate = createValidDate(expired_time);

  // Helper function to calculate durations safely
  const calculateDuration = (startDate: Date | null, endDate: Date | null): number => {
    if (!startDate || !endDate) {
      return 0;
    }
    try {
      return differenceInMinutes(endDate, startDate);
    } catch (_error) {
      return 0;
    }
  };
  // Calculate duration between start and end dates
  const duration = calculateDuration(startDate, endDate);
  let sessionDuration: number | undefined;
  // Calculate session duration if expired date is provided
  if (expiredDate) {
    sessionDuration = calculateDuration(startDate, expiredDate);
  }

  // Helper function to format time safely with timezone handling
  const formatTime = (startDate: Date | null, startTime: string): string => {
    if (!startDate) {
      return '';
    }
    try {
      const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(startTime);
      const utcDate = new Date(startTime + (hasTz ? '' : 'Z'));
      if (!isNaN(utcDate.getTime())) {
        const localDate = toZonedTime(utcDate, Intl.DateTimeFormat().resolvedOptions().timeZone);
        return format(localDate, 'yyyy-MM-dd HH:mm:ss');
      } else {
        return format(startDate, 'yyyy-MM-dd HH:mm:ss');
      }
    } catch (_error) {
      // Fallback to basic formatting if timezone conversion fails
      return format(startDate, 'yyyy-MM-dd HH:mm:ss');
    }
  };
  // Format time safely with timezone handling
  const formattedTime = formatTime(startDate, start_time);

  return { Icon, duration: duration || '0 sec', sessionDuration, formattedTime };
};

export const getDateRangeText = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) {
    return '';
  }

  const start = parseTimestamp(startDate);
  const end = parseTimestamp(endDate);
  const now = new Date();

  if (!start || !end) {
    return '';
  }

  const namedKey = getNamedPeriodTitleKey(startDate, endDate);
  if (namedKey) {
    return i18n.t(namedKey);
  }

  // Check if end date is today (calendar day, not 24-hour period)
  if (isSameDay(end, now)) {
    if (isSameDay(start, now)) {
      return i18n.t('analytics.periods.today');
    }
    // Use calendar days for more intuitive counting
    const days = differenceInCalendarDays(end, start);
    return i18n.t('analytics.last_days', { 0: days + 1 });
  }

  // Check if both dates are yesterday (calendar day)
  const yesterday = subDays(now, 1);
  if (isSameDay(start, yesterday) && isSameDay(end, yesterday)) {
    return i18n.t('analytics.periods.yesterday');
  }

  return `${format(start, 'MMM dd, yyyy')} - ${format(end, 'MMM dd, yyyy')}`;
};

export const convertTimestampToDate = (value: number | string) => {
  const date = format(new Date(value), 'yyyy-MM-dd HH:mm:ss');
  const utcDate = new Date(date + 'Z'); // Treat as UTC
  const localDate = toZonedTime(utcDate, Intl.DateTimeFormat().resolvedOptions().timeZone);
  return formatDistanceToNowStrict(localDate, { addSuffix: true });
};

export const getGrowthArrowColor = (value: number): string => {
  if (value > 0) {
    return 'var(--color-successful)';
  }
  if (value < 0) {
    return 'var(--color-destructive)';
  }
  return 'var(--color-foreground)';
};

/**
 * Converts start and end date strings into a full-day UTC range, adjusted to the
 * beginning and end of their respective UTC days. This function prevents errors from
 * invalid date strings.
 *
 * @param {string | undefined} start - The start date string (e.g., "2025-08-20").
 * @param {string | undefined} end - The end date string (e.g., "2025-08-21").
 * @returns {AnalyticsRange} An object with `startDate` (00:00:00.000 UTC) and
 * `endDate` (23:59:59.999 UTC) as ISO strings. Returns null for any invalid inputs.
 */
export const getAnalyticsRangeUTC = (start?: string, end?: string): AnalyticsRange => {
  const toUTCDayBoundary = (
    dateString: string | undefined,
    boundary: 'start' | 'end'
  ): string | null => {
    if (!dateString) {
      return null;
    }

    const trimmed = dateString.trim();
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
    const hasExplicitTime = /^\d{4}-\d{2}-\d{2}T/.test(trimmed);
    const hasTimezone = /Z|[+-]\d{2}:?\d{2}$/.test(trimmed);

    let parsedDate: Date;

    if (isDateOnly) {
      parsedDate = new Date(`${trimmed}T00:00:00.000Z`);
    } else if (hasExplicitTime && !hasTimezone) {
      parsedDate = new Date(`${trimmed}Z`);
    } else {
      parsedDate = new Date(trimmed);
    }

    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    if (boundary === 'start') {
      parsedDate.setUTCHours(0, 0, 0, 0);
    } else {
      parsedDate.setUTCHours(23, 59, 59, 999);
    }

    return parsedDate.toISOString();
  };

  return { startDate: toUTCDayBoundary(start, 'start'), endDate: toUTCDayBoundary(end, 'end') };
};

/**
 * Converts the given start and end date strings to local time, then to start of day and end of day,
 * and returns the date range in local format.
 * This is intended for the chat logs range. Will return whole **LOCAL** days.
 *
 * @param {string | undefined} start - The start date as a string. If null or undefined, startDate will be null.
 * @param {string | undefined} end - The end date as a string. If null or undefined, endDate will be null.
 * @returns {{ startDate: string | null, endDate: string | null }} - An object containing startDate and endDate
 *          in local format. Both values will be null if the corresponding input is null, undefined, or invalid.
 */
export const getAnalyticsRangeLocal = (start?: string, end?: string): AnalyticsRange => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  /**
   * Validates that a Date object is valid (not Invalid Date).
   * Invalid dates occur when parsing malformed strings like "invalid-date" or "2024/06/19".
   */
  const isValidDate = (date: Date): boolean => !isNaN(date.getTime());

  const toStartOfDayUTC = (input: string): string | null => {
    if (!input) {
      return null;
    }
    // Wrap in try-catch to handle any unexpected errors from date-fns-tz functions
    // This prevents runtime crashes from corrupted URL params or stored state
    try {
      // Date-only format (YYYY-MM-DD): construct explicit local midnight
      if (!input.includes('T')) {
        const result = fromZonedTime(`${input}T00:00:00.000`, timeZone);
        // Validate before calling toISOString() to prevent "Invalid time value" error
        if (!isValidDate(result)) {
          return null;
        }
        return result.toISOString();
      }
      // ISO format with time/timezone: parse the instant and convert to local timezone
      const instant = new Date(input);
      // Validate parsed date before further processing
      if (!isValidDate(instant)) {
        return null;
      }
      const local = toZonedTime(instant, timeZone);
      const localStart = startOfDay(local);
      return fromZonedTime(localStart, timeZone).toISOString();
    } catch {
      // Return null for any parsing errors instead of throwing
      return null;
    }
  };

  const toEndOfDayUTC = (input: string): string | null => {
    if (!input) {
      return null;
    }
    // Wrap in try-catch to handle any unexpected errors from date-fns-tz functions
    try {
      // Date-only format (YYYY-MM-DD): construct explicit local end of day
      if (!input.includes('T')) {
        const result = fromZonedTime(`${input}T23:59:59.999`, timeZone);
        // Validate before calling toISOString() to prevent "Invalid time value" error
        if (!isValidDate(result)) {
          return null;
        }
        return result.toISOString();
      }
      // ISO format with time/timezone: parse the instant and convert to local timezone
      const instant = new Date(input);
      // Validate parsed date before further processing
      if (!isValidDate(instant)) {
        return null;
      }
      const local = toZonedTime(instant, timeZone);
      const localEnd = endOfDay(local);
      return fromZonedTime(localEnd, timeZone).toISOString();
    } catch {
      // Return null for any parsing errors instead of throwing
      return null;
    }
  };

  return {
    startDate: start ? toStartOfDayUTC(start) : null,
    endDate: end ? toEndOfDayUTC(end) : null,
  };
};
